import { Plus, Trash2 } from 'lucide-react'
import { Button, Field, IconButton, Input, Select } from '@/components/common'
import { formatStartTime } from './nativePreviewModel'
import {
  PLAYER_PROP_MODES, PLAYER_PROP_SELECTIONS, PLAYER_PROP_TEMPLATES, columnsFromTemplate,
  knownMarketLabel, lineText, marketOptionLabel, nextPlayerPropColumn, sampleDirectory, suggestionsFromMarkets, templatesFor,
} from './playerPropBoardModel'
import { discoveryNote, leagueChoices, leagueKey, usePlayerPropMarkets } from './playerPropDiscovery'
import styles from './nativeSportsbook.module.css'
import { isLiveSource } from './catalogSources'

export function PlayerPropScopeControl({ target, screen, catalog, slug, disabled, onChange }) {
  const scope = target.scope || {}
  const hub = screen?.context?.kind === 'competition'
  const live = isLiveSource(catalog?.source)
  const discovery = usePlayerPropMarkets({ slug, catalog, sport: scope.sport, competition: scope.competition })
  const directory = live ? { events: discovery.data?.events || [], teams: discovery.data?.teams || [] } : sampleDirectory(scope.sport, scope.competition)
  const setScope = (next) => onChange('scope', next)
  const chooseLeague = (value) => {
    const league = leagueChoices(catalog, scope).find((candidate) => leagueKey(candidate.sport, candidate.competition) === value)
    if (!league) return
    setScope({ mode: 'competition', sport: league.sport, competition: league.competition })
    const template = templatesFor(league.sport, league.competition)[0]
    if (template) {
      const next = columnsFromTemplate(template)
      onChange('columns', next.columns)
      onChange('rank_column_id', next.rank_column_id)
      if (!target.title || PLAYER_PROP_TEMPLATES.some((candidate) => candidate.title === target.title)) onChange('title', template.title)
    }
  }
  const chooseMode = (mode) => {
    const base = { mode, sport: scope.sport, competition: scope.competition }
    if (mode === 'event') setScope({ ...base, event_id: directory.events[0]?.event_id || '' })
    else if (mode === 'team') setScope({ ...base, ...(directory.teams[0] ? { team: { participant_id: directory.teams[0].id, name: directory.teams[0].name } } : {}) })
    else setScope(base)
  }
  const knownEvent = directory.events.some((event) => event.event_id === scope.event_id)
  const knownTeam = directory.teams.some((team) => team.id === scope.team?.participant_id)
  const note = discoveryNote(live, discovery)
  return (
    <fieldset className={styles.playerPropScope} disabled={disabled}>
      <legend>Players from</legend>
      <Field label="League" help={hub ? "A board on a league hub uses the hub's league." : undefined}>
        {hub
          ? <Input size="compact" readOnly value={scope.competition || screen.context.competition} />
          : <Select size="compact" value={leagueKey(scope.sport, scope.competition)} onChange={(event) => chooseLeague(event.target.value)}>
            {leagueChoices(catalog, scope).map((league) => <option key={leagueKey(league.sport, league.competition)} value={leagueKey(league.sport, league.competition)}>{league.competition}</option>)}
          </Select>}
      </Field>
      <Field label="Which games">
        <Select size="compact" value={scope.mode || 'competition'} onChange={(event) => chooseMode(event.target.value)}>
          {PLAYER_PROP_MODES.map((mode) => <option key={mode.value} value={mode.value}>{mode.label}</option>)}
        </Select>
      </Field>
      {scope.mode === 'event' ? <Field label="Game">
        <Select size="compact" value={scope.event_id || ''} onChange={(event) => setScope({ ...scope, event_id: event.target.value })}>
          {!scope.event_id ? <option value="">Choose a game</option> : null}
          {scope.event_id && !knownEvent ? <option value={scope.event_id}>Game {scope.event_id} (not among the nearest games)</option> : null}
          {directory.events.map((event) => <option key={event.event_id} value={event.event_id}>{event.name}{event.start_time ? ` · ${formatStartTime(event.start_time)}` : ''}</option>)}
        </Select>
      </Field> : null}
      {scope.mode === 'team' ? <Field label="Team" help="Players the provider lists for this team in its next game.">
        <Select size="compact" value={scope.team?.participant_id || ''} onChange={(event) => {
          const team = directory.teams.find((candidate) => candidate.id === event.target.value)
          if (team) setScope({ mode: 'team', sport: scope.sport, competition: scope.competition, team: { participant_id: team.id, name: team.name } })
        }}>
          {!scope.team ? <option value="">Choose a team</option> : null}
          {scope.team && !knownTeam ? <option value={scope.team.participant_id}>{scope.team.name}</option> : null}
          {directory.teams.map((team) => <option key={team.id} value={team.id}>{team.name}</option>)}
        </Select>
      </Field> : null}
      {note ? <p className={styles.inspectorHint} role="note">{note}</p> : null}
    </fieldset>
  )
}

export function PlayerPropColumnsControl({ target, catalog, slug, disabled, onChange }) {
  const scope = target.scope || {}
  const columns = target.columns || []
  const live = isLiveSource(catalog?.source)
  const discovery = usePlayerPropMarkets({ slug, catalog, sport: scope.sport, competition: scope.competition, eventId: scope.mode === 'event' ? scope.event_id : undefined })
  const markets = live && discovery.data?.state === 'ready' ? discovery.data.markets : []
  const templates = templatesFor(scope.sport, scope.competition)
  const suggestions = suggestionsFromMarkets(markets, templates)
  const nextColumn = nextPlayerPropColumn(columns, markets)
  const rankId = target.rank_column_id || columns[0]?.id
  const setColumns = (next, nextRank = rankId) => {
    onChange('columns', next)
    onChange('rank_column_id', next.some((column) => column.id === nextRank) ? nextRank : next[0]?.id)
  }
  const apply = (key) => {
    const source = [...templates, ...suggestions].find((candidate) => candidate.key === key)
    if (!source) return
    const next = columnsFromTemplate(source)
    setColumns(next.columns, next.rank_column_id)
    if (!target.title || PLAYER_PROP_TEMPLATES.some((candidate) => candidate.title === target.title) || suggestions.some((candidate) => candidate.title === target.title)) onChange('title', source.title)
  }
  const update = (index, patch) => setColumns(columns.map((column, position) => {
    if (position !== index) return column
    const next = { ...column, ...patch }
    if (next.selection === 'participant' || next.line_millis === '') delete next.line_millis
    return next
  }))
  return (
    <fieldset className={styles.playerPropColumns} disabled={disabled}>
      <legend>Markets</legend>
      <Field label="Start from" help="Replaces the columns with a provider template or a board built from posted markets.">
        <Select size="compact" value="" onChange={(event) => apply(event.target.value)}>
          <option value="">Choose a template…</option>
          {templates.length ? <optgroup label="Provider templates">{templates.map((template) => <option key={template.key} value={template.key}>{template.name}</option>)}</optgroup> : null}
          {suggestions.length ? <optgroup label="Built from posted markets">{suggestions.map((suggestion) => <option key={suggestion.key} value={suggestion.key}>{suggestion.name}</option>)}</optgroup> : null}
        </Select>
      </Field>
      {columns.map((column, index) => (
        <div key={column.id} className={styles.playerPropColumn} data-testid="player-prop-column">
          <div className={styles.playerPropColumnHeader}>
            <Field label={`Column ${index + 1}`}><Input size="compact" value={column.label} maxLength={12} onChange={(event) => update(index, { label: event.target.value.toUpperCase() })} /></Field>
            <IconButton variant="danger" className={styles.rowIcon} label={`Remove column ${column.label}`} disabled={columns.length <= 1} onClick={() => setColumns(columns.filter((_, position) => position !== index))}><Trash2 size={14} /></IconButton>
          </div>
          <PropColumnFields column={column} markets={markets} onUpdate={(patch) => update(index, patch)} />
        </div>
      ))}
      {columns.length > 1 ? <Field label="Sort players by">
        <Select size="compact" value={rankId} onChange={(event) => onChange('rank_column_id', event.target.value)}>
          {columns.map((column, index) => <option key={column.id} value={column.id}>{column.label || `Column ${index + 1}`}</option>)}
        </Select>
      </Field> : null}
      {/* A new column needs a posted market to name, so it waits for discovery. */}
      <Button size="compact" variant="secondary" disabled={columns.length >= 3 || !nextColumn} onClick={() => {
        setColumns([...columns, { id: globalThis.crypto.randomUUID(), ...nextColumn }])
      }}><Plus size={14} aria-hidden="true" /> Add column</Button>
    </fieldset>
  )
}

/**
 * One column's provider market, pick and line, shared by player prop boards and
 * parlay builder groups. `onUpdate` receives a patch; a blank line means each
 * player's main line and is removed by the caller.
 */
export function PropColumnFields({ column, markets, onUpdate }) {
  const market = markets.find((candidate) => candidate.criterion_id === column.criterion_id)
  const selections = market ? PLAYER_PROP_SELECTIONS.filter((selection) => market.selections.includes(selection.value)) : PLAYER_PROP_SELECTIONS
  const lines = market?.lines || []
  const lineChoice = column.selection !== 'participant' && (lines.length > 0 || column.line_millis != null)
  return (<>
    <Field label="Market">
      <Select size="compact" value={column.criterion_id} onChange={(event) => {
        const next = markets.find((candidate) => candidate.criterion_id === event.target.value)
        const selection = next?.selections.includes(column.selection) ? column.selection : next?.selections[0] || column.selection
        onUpdate({ criterion_id: event.target.value, selection, line_millis: selection === 'yes' || selection === 'no' ? next?.lines[0]?.line_millis ?? '' : '' })
      }}>
        {!market ? <option value={column.criterion_id}>{knownMarketLabel(column.criterion_id)}</option> : null}
        {markets.map((candidate) => <option key={candidate.criterion_id} value={candidate.criterion_id}>{marketOptionLabel(candidate)}</option>)}
      </Select>
    </Field>
    <div className={styles.playerPropColumnPair}>
      <Field label="Pick">
        <Select size="compact" value={column.selection} onChange={(event) => onUpdate({ selection: event.target.value })}>
          {selections.map((selection) => <option key={selection.value} value={selection.value}>{selection.label}</option>)}
        </Select>
      </Field>
      {lineChoice ? <Field label="Line">
        <Select size="compact" value={column.line_millis ?? ''} onChange={(event) => onUpdate({ line_millis: event.target.value === '' ? '' : Number.parseInt(event.target.value, 10) })}>
          <option value="">Main line per player</option>
          {column.line_millis != null && !lines.some((line) => line.line_millis === column.line_millis) ? <option value={column.line_millis}>{lineText(column.line_millis)}</option> : null}
          {lines.map((line) => <option key={line.line_millis} value={line.line_millis}>{line.short_label || line.label} ({lineText(line.line_millis)})</option>)}
        </Select>
      </Field> : null}
    </div>
  </>)
}
